import React, { useState, useCallback } from "react";
import { Card, Button, Input, Progress, Alert, Switch, RadioGroup, Radio } from "@heroui/react";
import { useDropzone } from "react-dropzone";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { TagSelector } from "@/components/TagSelector/TagSelector";
import { parseFileUploadError, formatErrorForLogging } from "@/utils/fileUploadErrorParser";
import { validateFiles } from "@/utils/fileValidation";
import { logger } from "@/telemetry";

interface FileWithMetadata {
  id: string; // Add unique ID for stable keys
  file: File;
  description: string;
  tags: string[];
  progress: number;
  status?: string; // Status text to show user what's happening
  error?: string;
  private: boolean;
  parser?: string;
}

interface FiledropProps {
  onUploadComplete?: (files: FileWithMetadata[]) => void;
}

export const Filedrop: React.FC<FiledropProps> = ({ onUploadComplete }) => {
  const [files, setFiles] = useState<FileWithMetadata[]>([]);
  const [uploading, setUploading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [globalSuccess, setGlobalSuccess] = useState<string | null>(null);
  const { platformClient } = useWebuildAPIClient();
  const { user } = useAuth();
  const { t } = useTranslation();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setGlobalError(null); // Clear any existing errors

    // VALIDATE FILES BEFORE ADDING TO QUEUE
    const validationResult = await validateFiles(acceptedFiles);

    if (!validationResult.isValid) {
      // Create a combined error message for all validation failures
      const errorMessages = validationResult.errors.map((error) => error.message);
      const combinedMessage = `File validation failed:\n${errorMessages.join("\n")}`;

      // Show validation errors as global error
      setGlobalError(combinedMessage);

      return; // Block files from being added to queue
    }

    // If validation passes, add files to queue with unique IDs
    const newFiles = acceptedFiles.map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`, // Unique stable ID
      file,
      description: "",
      tags: [],
      progress: 0,
      status: "", // Initialize status field
      private: true,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDescriptionChange = (fileId: string, description: string) => {
    setFiles((prev) => prev.map((file) => (file.id === fileId ? { ...file, description } : file)));
  };

  const handleTagSelect = (fileId: string, tags: string[]) => {
    setFiles((prev) => prev.map((file) => (file.id === fileId ? { ...file, tags } : file)));
  };

  const handleParserChange = (fileId: string, value: string) => {
    setFiles((prev) =>
      prev.map((file) => (file.id === fileId ? { ...file, parser: value } : file)),
    );
  };

  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const clearSuccessfulFiles = () => {
    setFiles((prev) => prev.filter((f) => f.progress !== 100 || f.error));
    setGlobalSuccess(null);
  };

  const handleUpload = async () => {
    if (!platformClient) {
      setGlobalError("Platform client not initialized");

      return;
    }

    setUploading(true);
    setGlobalError(null);
    setGlobalSuccess(null);

    // Stage 1: Set all files to "uploading" state (10% progress)
    setFiles((prev) =>
      prev.map((f) => ({
        ...f,
        progress: 10,
        status: t("Uploading..."),
        error: undefined,
      })),
    );

    // Track file states throughout the process
    const fileStates = new Map<string, { progress: number; status?: string; error?: string }>();

    // Step 1: Upload all files
    const uploadResults = await Promise.allSettled(
      files.map(async (fileData) => {
        const fileId = fileData.id;

        try {
          const fileType = fileData.file.name.split(".").pop()?.toLowerCase() || "unknown";

          const response = await platformClient.files.upload({
            bodyUploadFileBackgroundPlatformV1DataFilesPost: {
              file: fileData.file,
              summary: fileData.description,
              tags: fileData.tags,
              fileType,
              chatId: "",
              private: fileData.private,
              parser: fileData.parser || "llm",
            },
          });

          // Upload complete - mark for processing stage
          fileStates.set(fileId, { progress: 50, status: t("Processing...") });

          return { fileId, jobId: response.job_id, uploadSuccess: true };
        } catch (error) {
          // Parse error with user-friendly message
          const parsedError = parseFileUploadError(error, fileData.file.name, fileData.file.size);

          // Log error with full context
          const logData = formatErrorForLogging(
            error,
            fileData.file.name,
            fileData.file.size,
            user?.id,
            "",
          );

          logger.warn(t("fileUpload.uploadFailed", { defaultValue: "Upload Failed" }), {
            logData: String(logData),
          });

          // Store error for final update
          fileStates.set(fileId, { progress: 0, error: parsedError.userMessage });

          return { fileId, uploadSuccess: false, error: parsedError.userMessage };
        }
      }),
    );

    // Update UI to show upload completion (50% + "Processing..." for successful uploads)
    setFiles((prev) =>
      prev.map((f) => {
        const state = fileStates.get(f.id);

        if (state) {
          return { ...f, progress: state.progress, status: state.status, error: state.error };
        }

        return f;
      }),
    );

    // Step 2: Poll job statuses for successfully uploaded files
    const successfulUploads = uploadResults
      .filter((r) => r.status === "fulfilled")
      .map((r) => r.value as { fileId: string; jobId?: string; uploadSuccess: boolean })
      .filter((v) => v.uploadSuccess && v.jobId);

    if (successfulUploads.length > 0) {
      // Poll all job statuses in parallel (no UI updates during this)
      await Promise.allSettled(
        successfulUploads.map(async ({ fileId, jobId }) => {
          try {
            let jobStatus = await platformClient.files.getJobStatus({ jobId: jobId! });

            while (jobStatus.status === "pending") {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              jobStatus = await platformClient.files.getJobStatus({ jobId: jobId! });
            }

            // Job complete - mark for final update
            fileStates.set(fileId, { progress: 100, status: t("Complete") });
          } catch (error) {
            logger.warn("Job status polling failed", { error: String(error) });
            // Job polling failed - mark error for final update
            fileStates.set(fileId, { progress: 0, error: t("Processing failed") });
          }
        }),
      );
    }

    // Final update: Apply all final states at once
    setFiles((prev) =>
      prev.map((f) => {
        const state = fileStates.get(f.id);

        if (state) {
          // File had an error or completed processing
          return { ...f, progress: state.progress, status: state.status, error: state.error };
        } else {
          // File uploaded successfully but no explicit final state
          const wasSuccessful = successfulUploads.some(({ fileId }) => fileId === f.id);

          return {
            ...f,
            progress: wasSuccessful ? 100 : f.progress,
            status: wasSuccessful ? t("Complete") : f.status,
          };
        }
      }),
    );

    // Count successes and failures
    const successfulCount =
      Array.from(fileStates.entries()).filter(([_, state]) => state.progress === 100).length +
      successfulUploads.filter(({ fileId }) => !fileStates.has(fileId)).length;
    const failedCount = files.length - successfulCount;

    if (failedCount > 0) {
      setGlobalError(t("fileUpload.failedCount", { count: failedCount }));
    }

    if (successfulCount > 0) {
      setGlobalSuccess(t("fileUpload.successCount", { count: successfulCount }));
    }

    if (onUploadComplete) {
      // Extract successful file IDs - both explicit 100% and successful uploads without explicit state
      const explicitSuccessIds = Array.from(fileStates.entries())
        .filter(([_, state]) => state.progress === 100)
        .map(([fileId, _]) => fileId);

      const implicitSuccessIds = successfulUploads
        .filter(({ fileId }) => !fileStates.has(fileId))
        .map(({ fileId }) => fileId);

      const allSuccessfulIds = [...explicitSuccessIds, ...implicitSuccessIds];
      const successfullyUploadedFiles = files.filter((f) => allSuccessfulIds.includes(f.id));

      onUploadComplete(successfullyUploadedFiles);
    }

    // Don't automatically clear files - let user dismiss them or upload new batch
    // This prevents the disappearing/reappearing issue
    setUploading(false);
  };

  return (
    <div className="w-full max-w-4xl mx-auto" data-id="filedrop-component">
      {globalError && (
        <Alert
          aria-label="Global error"
          className="mb-4"
          color="danger"
          data-id="global-error-alert"
        >
          {globalError}
        </Alert>
      )}
      {globalSuccess && (
        <Alert
          aria-label="Global success"
          className="mb-4"
          color="success"
          data-id="global-success-alert"
        >
          {globalSuccess}
        </Alert>
      )}

      <div
        {...getRootProps()}
        aria-label="Dropzone"
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          ${isDragActive ? "border-primary bg-primary/10" : "border-gray-300"}`}
        data-id="file-dropzone"
      >
        <input data-id="filedrop-input" {...getInputProps()} />
        <Icon
          aria-label="Upload icon"
          className="mx-auto h-12 w-12 text-gray-400"
          icon="solar:upload-linear"
        />
        <p className="mt-2 text-sm text-gray-600">
          {isDragActive
            ? "Drop the files here..."
            : "Drag 'n' drop files here, or click to select files"}
        </p>
      </div>

      {files.length > 0 && (
        <div className="mt-6 space-y-4" data-id="file-list">
          {files.map((fileData, index) => {
            const fileExtension = fileData.file.name.split(".").pop()?.toLowerCase();
            const isImage =
              fileExtension &&
              ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(fileExtension);

            return (
              <Card key={fileData.id} className="p-4" data-id={`file-card-${index}`}>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium" data-id={`file-name-${index}`}>
                      {fileData.file.name}
                    </span>
                    <Button
                      isIconOnly
                      aria-label="Remove file"
                      size="sm"
                      variant="light"
                      onPress={() => removeFile(fileData.id)}
                    >
                      <Icon className="text-lg" icon="gravity-ui:x" />
                    </Button>
                  </div>

                  {fileData.error && (
                    <Alert
                      aria-label={`Error for ${fileData.file.name}`}
                      className="mt-2"
                      color="danger"
                      data-id={`file-error-${index}`}
                    >
                      {fileData.error}
                    </Alert>
                  )}

                  <Input
                    aria-label={`Description for ${fileData.file.name}`}
                    data-id={`description-input-${index}`}
                    label="Description"
                    placeholder="Add a description for this file"
                    value={fileData.description}
                    onValueChange={(value) => handleDescriptionChange(fileData.id, value)}
                  />
                  {isImage && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Parser</p>
                      <div className="flex items-center space-x-4">
                        <RadioGroup
                          aria-label="Select parser type"
                          data-id={`parser-radiogroup-${index}`}
                          defaultValue="llm"
                          value={fileData.parser || "llm"}
                          onValueChange={(value) => handleParserChange(fileData.id, value)}
                        >
                          <Radio data-id={`parser-llm-radio-${index}`} value="llm">
                            LLM
                          </Radio>
                          <Radio data-id={`parser-ocr-radio-${index}`} value="ocr">
                            OCR
                          </Radio>
                        </RadioGroup>
                      </div>
                    </div>
                  )}

                  <TagSelector
                    label="Tags"
                    selectedTags={fileData.tags}
                    showAddNewTag={true}
                    onTagsChange={(tags) => handleTagSelect(fileData.id, tags)}
                  />

                  <div className="flex items-center space-x-2 mt-2">
                    <Switch
                      aria-label={`Toggle public for ${fileData.file.name}`}
                      checked={!fileData.private}
                      data-id={`public-toggle-switch-${index}`}
                      id={`public-toggle-${index}`}
                      onChange={() => {
                        setFiles((prev) =>
                          prev.map((f) =>
                            f.id === fileData.id ? { ...f, private: !f.private } : f,
                          ),
                        );
                      }}
                    />
                    <span className="text-xs text-gray-500">
                      {fileData.private ? t("Only you can access") : t("Shared with company")}
                    </span>
                  </div>

                  {fileData.progress > 0 && !fileData.error && (
                    <Progress
                      aria-label={fileData.status || t("Processing...")}
                      color={fileData.progress === 100 ? "success" : "primary"}
                      label={fileData.status || t("Processing...")}
                      showValueLabel={true}
                      size="sm"
                      value={fileData.progress}
                    />
                  )}

                  {fileData.error && (
                    <Alert color="danger">
                      <div className="flex items-center gap-2">
                        <Icon icon="gravity-ui:circle-cross-fill" />
                        <span>{fileData.error}</span>
                      </div>
                    </Alert>
                  )}
                </div>
              </Card>
            );
          })}

          <div className="flex gap-2">
            {files.some((f) => f.progress === 0) && (
              <Button
                aria-label="Upload files"
                className="flex-1 btn-brand-primary"
                data-id="upload-files-button"
                isDisabled={files.some((f) => f.error)}
                isLoading={uploading}
                onPress={handleUpload}
              >
                {uploading ? t("Uploading...") : t("Upload Files")}
              </Button>
            )}
            {files.some((f) => f.progress === 100 && !f.error) && (
              <Button
                aria-label="Clear successful uploads"
                className={files.some((f) => f.progress === 0) ? "" : "flex-1"}
                color="success"
                data-id="clear-successful-button"
                variant="flat"
                onPress={clearSuccessfulFiles}
              >
                {t("Clear Successful")}
              </Button>
            )}
            {files.every((f) => f.progress === 100 || f.error) && files.length > 0 && (
              <Button
                aria-label="Upload more files"
                className="flex-1 btn-brand-primary"
                data-id="upload-more-button"
                onPress={() => setFiles([])}
              >
                {t("Upload More Files")}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
